import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import {
  Plus,
  FileText,
  Loader2,
  AlertCircle,
  Clock,
  CheckCircle,
  Mic
} from 'lucide-react';
import Navbar from './shared/Navbar.jsx';
import { sharedStyles, createGlassCard, createButton } from './shared/sharedStyles.js';
import { useApi } from '../utils/api.js';

/**
 * GuestDashboard - Dashboard for guests looking for podcast appearances
 *
 * GUEST-SIDE mirror of Dashboard.jsx. Shows show matches, credit balance,
 * and quick actions for finding new shows.
 */
const GuestDashboard = () => {
  const navigate = useNavigate();
  const { user, isLoaded } = useUser();
  const { makeRequest } = useApi();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);
  const [matches, setMatches] = useState([]);
  const [profile, setProfile] = useState(null);
  const [totalShows, setTotalShows] = useState(0);
  const [creditBalance, setCreditBalance] = useState(null);
  const [generatingMatch, setGeneratingMatch] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchDashboardData = async () => {
      if (!isLoaded || !user) {
        console.log('⏳ GuestDashboard waiting for Clerk... isLoaded:', isLoaded, 'user:', !!user);
        return;
      }

      console.log('🎯 GuestDashboard: Starting data fetch for user:', user.id);

      try {
        setLoading(true);
        setError(null);

        console.log('📡 Fetching guest dashboard data in parallel...');

        // Fetch all data in parallel
        const [userResponse, matchesResponse, profileResponse, creditsResponse] = await Promise.all([
          makeRequest('users/me').then(res => {
            console.log('✅ User data fetched:', res);
            return res;
          }),
          makeRequest('guest/show-matches').catch(err => {
            console.warn('⚠️ Non-critical error fetching matches:', err);
            return [];
          }),
          makeRequest('guest/profile').catch(err => {
            console.warn('⚠️ Non-critical error fetching profile:', err);
            return null;
          }),
          makeRequest('credits/balance').catch(err => {
            console.warn('⚠️ Non-critical error fetching credit balance:', err);
            return { trial: 0, basic: 0, pro: 0, total: 0, is_byok: false };
          })
        ]);

        console.log('📊 All data fetched successfully');

        if (!isMounted) {
          console.log('⚠️ Component unmounted, aborting');
          return;
        }

        // Validate critical user data
        if (!userResponse || !userResponse.clerk_user_id) {
          throw new Error('Invalid user data received from server');
        }

        // Calculate total shows found
        const showCount = matchesResponse.reduce(
          (acc, match) => acc + (match.recommended_shows?.length || 0),
          0
        );

        // Set all state at once
        setUserData(userResponse);
        setMatches(matchesResponse);
        setProfile(profileResponse);
        setTotalShows(showCount);
        setCreditBalance(creditsResponse);

        console.log('📦 State updated:', {
          subscription_tier: userResponse.subscription_tier,
          is_lifetime: userResponse.is_lifetime,
          has_profile: !!profileResponse,
          matches_count: matchesResponse.length
        });

        // Check subscription requirement (production only)
        if (import.meta.env.PROD) {
          const hasSubscription = userResponse.subscription_tier && (
            userResponse.is_lifetime || userResponse.stripe_subscription_id
          );

          console.log('🔐 Subscription check (PROD):', hasSubscription);

          if (!hasSubscription) {
            console.log('❌ No subscription, redirecting to pricing...');
            navigate('/');
            setTimeout(() => {
              document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
            return;
          }
        }

        // Redirect users who haven't completed onboarding to intake form
        if (!profileResponse) {
          console.log('📝 No guest profile found, redirecting to intake form...');
          navigate('/guest/intake-form');
          return;
        }

        console.log('✅ GuestDashboard loaded successfully!');

      } catch (err) {
        console.error('❌ Critical error loading guest dashboard:', err);

        if (!isMounted) return;
        setError(err.message || 'Failed to load dashboard. Please try refreshing the page.');
      } finally {
        if (isMounted) {
          console.log('✅ Setting loading to false');
          setLoading(false);
        }
      }
    };

    fetchDashboardData();

    return () => {
      isMounted = false;
    };
  }, [isLoaded, user, navigate, makeRequest]);

  // Loading state
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: sharedStyles.colors.background }}>
        <Navbar />
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: '1rem',
        }}>
          <Loader2 size={48} style={{
            color: sharedStyles.colors.primary,
            animation: 'spin 1s linear infinite',
          }} />
          <p style={{ color: sharedStyles.colors.textSecondary }}>
            Loading your dashboard...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{ minHeight: '100vh', background: sharedStyles.colors.background }}>
        <Navbar />
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: '1.5rem',
          padding: '2rem',
        }}>
          <div style={{
            ...createGlassCard('default'),
            padding: '2rem',
            maxWidth: '500px',
            textAlign: 'center',
          }}>
            <AlertCircle size={48} style={{
              color: sharedStyles.colors.error,
              marginBottom: '1rem',
            }} />
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              color: sharedStyles.colors.textPrimary,
              marginBottom: '0.5rem',
            }}>
              Error Loading Dashboard
            </h2>
            <p style={{ color: sharedStyles.colors.textSecondary }}>
              {error}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const hasCompletedOnboarding = !!profile;
  const hasMatches = matches.length > 0;

  // Handle finding new shows
  const handleFindShows = async () => {
    if (!hasCompletedOnboarding) {
      navigate('/guest/intake-form');
      return;
    }

    setGeneratingMatch(true);

    try {
      const response = await makeRequest('guest/show-matches', {
        method: 'POST',
        body: JSON.stringify({})
      });

      setGeneratingMatch(false);
      navigate(`/guest/show-matches/${response.show_match_id}`);
    } catch (error) {
      console.error('Error finding shows:', error);

      if (error.message.includes('402')) {
        alert('No credits available. Purchase credits to find more shows.');
      } else if (error.message.includes('500')) {
        alert('The AI service encountered a temporary error. Please try again in a few seconds.');
      } else {
        alert(`Failed to find shows: ${error.message}`);
      }
      setGeneratingMatch(false);
    }
  };

  return (
    <div style={sharedStyles.pageContainer}>
      <Navbar />

      <div style={{
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        padding: '1rem 4rem',
        overflow: 'visible'
      }}>
        <div style={{
          display: 'flex',
          gap: '1rem',
          maxWidth: '1200px',
          width: '100%',
          alignItems: 'stretch',
          maxHeight: 'calc(100vh - 200px)',
          minHeight: 'fit-content'
        }}>
          {/* Left Sidebar - Recent Matches */}
          {hasCompletedOnboarding && (
            <div style={{
              width: '320px',
              ...createGlassCard(),
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '100%',
              overflow: 'hidden'
            }}>
              {/* Panel Header */}
              <div style={{
                padding: '1rem 1.25rem',
                borderBottom: `1px solid ${sharedStyles.colors.primary}20`,
                background: sharedStyles.colors.surfaceAlt
              }}>
                <h2 style={{
                  fontSize: '1.125rem',
                  fontWeight: '600',
                  color: sharedStyles.colors.textPrimary,
                  margin: 0
                }}>
                  Recent Searches
                </h2>
              </div>

              {/* Matches List */}
              <div style={{
                padding: '0.75rem 1rem',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'auto',
                gap: '0.75rem'
              }}>
                {hasMatches ? (
                  matches.map((match) => {
                    const matchDate = new Date(match.created_at);
                    const showCount = match.recommended_shows?.length || 0;

                    return (
                      <div
                        key={match.id}
                        onClick={() => navigate(`/guest/show-matches/${match.id}`)}
                        style={{
                          padding: '1rem',
                          background: match.is_completed ? sharedStyles.colors.surfaceAlt : `${sharedStyles.colors.error}10`,
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          border: match.is_completed ? `1px solid ${sharedStyles.colors.primary}20` : `1px solid ${sharedStyles.colors.error}30`,
                          opacity: match.is_completed ? 1 : 0.8
                        }}
                        onMouseEnter={(e) => {
                          if (match.is_completed) {
                            e.currentTarget.style.background = `${sharedStyles.colors.primary}10`;
                            e.currentTarget.style.borderColor = `${sharedStyles.colors.primary}30`;
                          } else {
                            e.currentTarget.style.background = `${sharedStyles.colors.error}20`;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (match.is_completed) {
                            e.currentTarget.style.background = sharedStyles.colors.surfaceAlt;
                            e.currentTarget.style.borderColor = `${sharedStyles.colors.primary}20`;
                          } else {
                            e.currentTarget.style.background = `${sharedStyles.colors.error}10`;
                          }
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                          <h3 style={{
                            fontSize: '0.95rem',
                            fontWeight: '600',
                            color: sharedStyles.colors.textPrimary,
                            flex: 1
                          }}>
                            Show Search
                          </h3>
                          {!match.is_completed && (
                            <div
                              title="This search did not complete successfully. Click to view details."
                              style={{
                                flexShrink: 0,
                                marginLeft: '0.5rem',
                                cursor: 'help'
                              }}
                            >
                              <AlertCircle
                                size={16}
                                style={{
                                  color: sharedStyles.colors.error
                                }}
                              />
                            </div>
                          )}
                        </div>
                        {!match.is_completed && (
                          <p style={{
                            fontSize: '0.7rem',
                            color: sharedStyles.colors.error,
                            fontWeight: '500',
                            marginBottom: '0.5rem'
                          }}>
                            Search Failed
                          </p>
                        )}
                        <p style={{
                          fontSize: '0.75rem',
                          color: sharedStyles.colors.textSecondary,
                          marginBottom: '0.5rem'
                        }}>
                          {showCount} show{showCount !== 1 ? 's' : ''} found
                        </p>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          color: sharedStyles.colors.textTertiary,
                          fontSize: '0.7rem'
                        }}>
                          <Clock size={12} />
                          <span>
                            {matchDate.toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </span>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div style={{
                    textAlign: 'center',
                    padding: '2rem 1rem',
                    color: sharedStyles.colors.textSecondary
                  }}>
                    <FileText size={32} style={{
                      color: sharedStyles.colors.textTertiary,
                      marginBottom: '0.5rem',
                      opacity: 0.5
                    }} />
                    <p style={{ fontSize: '0.875rem' }}>
                      No searches yet
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Main Content Area */}
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto'
          }}>
            {/* Welcome Header */}
            <div style={{
              marginBottom: '2rem',
            }}>
              <h1 style={{
                fontSize: '2.5rem',
                fontWeight: 'bold',
                color: sharedStyles.colors.textPrimary,
                marginBottom: '0.5rem',
              }}>
                Welcome back, {user?.firstName || 'there'}! 👋
              </h1>
              <p style={{
                fontSize: '1.125rem',
                color: sharedStyles.colors.textSecondary,
              }}>
                {hasCompletedOnboarding
                  ? 'Ready to discover podcasts looking for guests like you?'
                  : 'Let\'s find great podcasts for you to appear on!'}
              </p>
            </div>

            {/* Stats Cards */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '1.5rem',
              marginBottom: '2rem',
            }}>
              {/* Credit Balance */}
              <div
                style={{
                  ...createGlassCard('hover'),
                  padding: '1.5rem',
                  cursor: 'pointer',
                }}
                onClick={() => navigate('/purchase-credits')}
                title="Click to purchase more credits"
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                  <FileText size={20} style={{ color: sharedStyles.colors.primary }} />
                  <h3 style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: sharedStyles.colors.textSecondary,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}>
                    Credit Balance
                  </h3>
                </div>
                <p style={{
                  fontSize: '1.875rem',
                  fontWeight: 'bold',
                  color: sharedStyles.colors.textPrimary,
                }}>
                  {creditBalance?.is_byok
                    ? '∞'
                    : (creditBalance?.total || 0)}
                </p>
                {creditBalance && !creditBalance.is_byok && (
                  <div style={{
                    marginTop: '0.75rem',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.375rem',
                  }}>
                    {creditBalance.trial > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: sharedStyles.colors.textSecondary }}>
                        <span>Trial:</span>
                        <span style={{ fontWeight: '600' }}>{creditBalance.trial}</span>
                      </div>
                    )}
                    {creditBalance.basic > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: sharedStyles.colors.textSecondary }}>
                        <span>Basic:</span>
                        <span style={{ fontWeight: '600' }}>{creditBalance.basic}</span>
                      </div>
                    )}
                    {creditBalance.pro > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: sharedStyles.colors.textSecondary }}>
                        <span>Pro:</span>
                        <span style={{ fontWeight: '600' }}>{creditBalance.pro}</span>
                      </div>
                    )}
                  </div>
                )}
                {creditBalance?.is_byok && (
                  <p style={{
                    marginTop: '0.5rem',
                    fontSize: '0.75rem',
                    color: sharedStyles.colors.textSecondary,
                  }}>
                    BYOK Enabled - Unlimited
                  </p>
                )}
                {!creditBalance?.is_byok && creditBalance?.total === 0 && (
                  <button
                    style={{
                      ...createButton('secondary', {
                        width: '100%',
                        justifyContent: 'center',
                        padding: '0.5rem',
                        fontSize: '0.75rem',
                        marginTop: '0.75rem',
                      }),
                    }}
                  >
                    Buy Credits
                  </button>
                )}
              </div>

              {/* Total Shows Found */}
              <div style={{
                ...createGlassCard('hover'),
                padding: '1.5rem',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                  <CheckCircle size={20} style={{ color: sharedStyles.colors.success }} />
                  <h3 style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: sharedStyles.colors.textSecondary,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}>
                    Total Shows Found
                  </h3>
                </div>
                <p style={{
                  fontSize: '1.875rem',
                  fontWeight: 'bold',
                  color: sharedStyles.colors.textPrimary,
                }}>
                  {totalShows}
                </p>
              </div>
            </div>

            {/* Quick Actions */}
            {hasCompletedOnboarding && (
              <div style={{
                display: 'flex',
                gap: '1rem',
                justifyContent: 'center',
                marginBottom: '3rem',
              }}>
                {/* Primary CTA - Find New Shows */}
                <button
                  onClick={handleFindShows}
                  disabled={generatingMatch}
                  title={generatingMatch ? "You can close this page and come back later - your search will continue in the background" : ""}
                  style={{
                    ...createButton('primary'),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    fontSize: '1rem',
                    padding: '1rem 1.5rem',
                    opacity: generatingMatch ? 0.8 : 1,
                    cursor: generatingMatch ? 'wait' : 'pointer',
                    position: 'relative'
                  }}
                >
                  {generatingMatch ? (
                    <>
                      <Loader2
                        size={20}
                        style={{
                          animation: 'spin 1s linear infinite'
                        }}
                      />
                      <span>Finding Shows... (30-60s)</span>
                    </>
                  ) : (
                    <>
                      <Plus size={20} />
                      Find New Shows
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Empty state for users who haven't completed onboarding */}
            {!hasCompletedOnboarding && (
              <div style={{
                ...createGlassCard('default'),
                padding: '3rem',
                textAlign: 'center',
              }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  margin: '0 auto 1.5rem',
                  background: `linear-gradient(135deg, ${sharedStyles.colors.primary}, ${sharedStyles.colors.primaryDark})`,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Mic size={40} style={{ color: 'white' }} />
                </div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '600',
                  color: sharedStyles.colors.textPrimary,
                  marginBottom: '0.5rem',
                }}>
                  Welcome to Conviviality! 🎙️
                </h3>
                <p style={{
                  color: sharedStyles.colors.textSecondary,
                  marginBottom: '2rem',
                  maxWidth: '500px',
                  margin: '0 auto 2rem',
                  lineHeight: '1.6',
                }}>
                  Let's find the perfect podcasts for you to appear on. Our AI will analyze your
                  expertise and discover shows that are looking for guests like you.
                </p>
                <button
                  onClick={() => navigate('/guest/intake-form')}
                  style={{
                    ...createButton('primary'),
                    fontSize: '1.125rem',
                    padding: '1rem 2rem',
                  }}
                >
                  Create Your Profile
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuestDashboard;
